import { useEffect, useMemo, useRef, useState } from "react";
import { getSocs } from "../repository";
import type { CtcaeTerm } from "../types";

type Props = { records: CtcaeTerm[]; open: boolean; onClose: () => void; onSelect: (record: CtcaeTerm) => void };

export function BrowsePanel({ records, open, onClose, onSelect }: Props) {
  const [selectedSoc, setSelectedSoc] = useState<string>();
  const closeRef = useRef<HTMLButtonElement>(null);
  const socs = useMemo(() => getSocs(records), [records]);
  const terms = useMemo(
    () => records.filter((record) => record.soc === selectedSoc).sort((a, b) => a.term.localeCompare(b.term)),
    [records, selectedSoc],
  );
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const handler = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.classList.remove("modal-open");
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-backdrop" type="button" aria-label="Close category browser" onClick={onClose} />
      <section className="browse-panel" role="dialog" aria-modal="true" aria-labelledby="browse-title">
        <header className="browse-header">
          {selectedSoc ? <button className="back-button" type="button" onClick={() => setSelectedSoc(undefined)}>← Categories</button> : <span />}
          <button className="icon-button" ref={closeRef} type="button" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="browse-content">
          <span className="eyebrow">CTCAE categories</span>
          <h2 id="browse-title">{selectedSoc ?? "Browse by System Organ Class"}</h2>
          <p>{selectedSoc ? `${terms.length} terms in this CTCAE version.` : "Choose a source category, then select a canonical CTCAE term."}</p>
          <div className={selectedSoc ? "browse-terms" : "soc-list"}>
            {selectedSoc
              ? terms.map((record) => <button type="button" key={record.id} onClick={() => onSelect(record)}><span>{record.term}</span><b>›</b></button>)
              : socs.map((soc) => {
                  const count = records.filter((record) => record.soc === soc).length;
                  return <button type="button" key={soc} onClick={() => setSelectedSoc(soc)}><span>{soc}<small>{count} terms</small></span><b>›</b></button>;
                })}
          </div>
        </div>
      </section>
    </div>
  );
}
